import { Avatar } from 'antd';
import { getUserProfilePictureUrl } from '../../../common/userHelper';
import { UserOutlined } from '@ant-design/icons';
import { WorkspaceAccessResponse } from '../../../api/workspace';
import colorTokens from '../../../constants/design_system/colors';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import { WorkspaceEstimationType } from '../../../types/Workspace';

export enum PriorityOptions {
  NoPriority = 'No priority',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Urgent = 'Urgent',
}

export const getPriorityIndex = (priority: PriorityOptions): number => {
  const priorityOrder = [
    PriorityOptions.NoPriority,
    PriorityOptions.Low,
    PriorityOptions.Medium,
    PriorityOptions.High,
    PriorityOptions.Urgent,
  ];
  return priorityOrder.indexOf(priority);
};

type FlagProps = {
  colorFill: string;
  label: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export const PriorityFlagPropsMap: Record<PriorityOptions, FlagProps> = {
  [PriorityOptions.NoPriority]: {
    colorFill: colorTokens.neutral[700],
    label: PriorityOptions.NoPriority,
  },
  [PriorityOptions.Low]: { colorFill: '#BDBFCD', label: PriorityOptions.Low },
  [PriorityOptions.Medium]: {
    colorFill: '#1D7AFC',
    label: PriorityOptions.Medium,
  },
  [PriorityOptions.High]: { colorFill: '#FAA53D', label: PriorityOptions.High },
  [PriorityOptions.Urgent]: {
    colorFill: '#EF4A63',
    label: PriorityOptions.Urgent,
  },
};

// implement above function with all parameters
type UserAvatarProps = {
  assignee: number | undefined;
  usersWithAccessToSpace: WorkspaceAccessResponse[];
};
export const UserAvatar = ({ assignee, usersWithAccessToSpace }: UserAvatarProps) => {
  if (assignee) {
    // this doesn't work for invited users
    const user = usersWithAccessToSpace.find((u) => u.userId === Number(assignee));
    if (user) {
      // Defaulting to email in case no full name is set or user ID if email is not found for some reason
      const label = user.User.fullName || user.User.email || user.userId;
      return (
        <>
          <CustomTooltip title={label}>
            <Avatar
              size={24}
              src={getUserProfilePictureUrl(user.User.profilePicturePath)}
              className="flex justify-center items-center bg-neutral-200"
            >
              <div className="text-center text-slate-500 text-[12px] font-semibold font-['Figtree'] leading-3">
                {(user.User.fullName || user.User.email)?.slice(0, 1)}
              </div>
            </Avatar>
          </CustomTooltip>
        </>
      );
    } else {
      return (
        <CustomTooltip title="User without access">
          <Avatar
            size={24}
            className="bg-neutral-200"
            icon={<UserOutlined style={{ color: colorTokens.neutral[800] }} />}
          />
        </CustomTooltip>
      );
    }
  }
  return (
    <CustomTooltip title="Click to assign owner">
      <Avatar
        size={24}
        className="bg-neutral-200"
        icon={<UserOutlined style={{ color: colorTokens.neutral[800] }} />}
      />
    </CustomTooltip>
  );
};

export const assigneeColumnWidth = 120;
export const tagWidth = 82;
export const flagWidth = 23;

export const TaskEstimateMapping = (estimationType: WorkspaceEstimationType): { [key: number]: string } => {
  switch (estimationType) {
    case WorkspaceEstimationType.storyPoints:
      return {
        1: '1',
        2: '2',
        3: '3',
        4: '5',
        5: '8',
        6: '13',
        7: '21',
      };
    case WorkspaceEstimationType.tshirtSizes:
      return {
        1: 'XS',
        2: 'S',
        3: 'M',
        4: 'L',
        5: 'XL',
      };
  }
};

export const TaskEstimateLongMapping = (estimationType: WorkspaceEstimationType): { [key: number]: string } => {
  switch (estimationType) {
    case WorkspaceEstimationType.storyPoints:
      return {
        1: '1 (Story Point)',
        2: '2 (Story Points)',
        3: '3 (Story Points)',
        4: '5 (Story Points)',
        5: '8 (Story Points)',
        6: '13 (Story Points)',
        7: '21 (Story Points)',
      };
    case WorkspaceEstimationType.tshirtSizes:
      return {
        1: 'XS - Extra Small',
        2: 'S - Small',
        3: 'M - Medium',
        4: 'L - Large',
        5: 'XL - Extra Large',
      };
  }
};
